import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useActiveOrg } from "context/ActiveOrgContext";
import { RegularButton, RoundButton } from "components/Button";
import { ReactComponent as PawIcon } from "assets/icons/paw.svg";
import { ReactComponent as CallIcon } from "assets/icons/call.svg";
import { ReactComponent as BuildingIcon } from "assets/icons/building.svg";
import { ReactComponent as AppointmentIcon } from "assets/icons/appointment.svg";
import { ReactComponent as EuroIcon } from "assets/icons/euro.svg";
import PatientsList from "./PatientsList";
import PhoneNumbersList from "./PhoneNumbersList";
import AddressesList from "./AddressesList";
import Appointments from "./Appointments";
import Bills from "./Bills";
import LinkPatientDialog from "./LinkPatientDialog";

type SelectedIcon = "paw" | "call" | "building" | "appointment" | "euro";

const Components = {
  paw: PatientsList,
  call: PhoneNumbersList,
  building: AddressesList,
  appointment: Appointments,
  euro: Bills,
};

const DetailSelector = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const [selected, setSelected] = useState<SelectedIcon>("paw");
  const [linkOpen, setLinkOpen] = useState(false);
  // Bumped after a link succeeds so children that key off it remount and re-fetch.
  const [refreshKey, setRefreshKey] = useState(0);

  const contactId = id ? Number(id) : NaN;
  const contactIdValid = Number.isFinite(contactId) && contactId > 0;

  const SelectedComponent = Components[selected];

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap gap-4">
          <RoundButton
            Icon={PawIcon}
            size="large"
            selected={selected === "paw"}
            onClick={() => setSelected("paw")}
          />

          <RoundButton
            Icon={CallIcon}
            size="large"
            iconLineType="fill"
            selected={selected === "call"}
            onClick={() => setSelected("call")}
          />

          <RoundButton
            Icon={BuildingIcon}
            size="large"
            iconLineType="fill"
            selected={selected === "building"}
            onClick={() => setSelected("building")}
          />

          <RoundButton
            Icon={AppointmentIcon}
            size="large"
            iconLineType="fill"
            selected={selected === "appointment"}
            onClick={() => setSelected("appointment")}
          />

          <RoundButton
            Icon={EuroIcon}
            size="large"
            iconLineType="fill"
            selected={selected === "euro"}
            onClick={() => setSelected("euro")}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <RegularButton
            color="Secondary"
            onClick={() =>
              contactIdValid
                ? navigate(`/app/patients/new/${contactId}`)
                : navigate("/app/patients/new")
            }
          >
            <span>Add New Patient</span>
          </RegularButton>
          <RegularButton
            color="Secondary"
            disabled={!contactIdValid}
            onClick={() => setLinkOpen(true)}
          >
            <span>Link Existing Patient</span>
          </RegularButton>
        </div>
      </div>

      <SelectedComponent key={refreshKey} />

      <LinkPatientDialog
        open={linkOpen}
        orgId={orgId}
        contactId={contactIdValid ? contactId : 0}
        onClose={() => setLinkOpen(false)}
        onLinked={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default DetailSelector;
